import React from "react";

export default function CompliancePolicy() {
  return (
    <div className="container py-5">

      <h1 className="fw-bold mb-4">Compliance & Data Security Policy</h1>

      <p className="text-muted" style={{ fontSize: "16px" }}>
        This Compliance & Data Security Policy outlines the operational, security, and regulatory
        standards followed by <strong>SWIFTSHIP</strong> (“Company”, “we”, “our”).  
        Our goal is to ensure safe, lawful, and fully compliant logistics operations across the
        United States and internationally.
        <br /><br />
        SWIFTSHIP adheres to U.S. Federal, State, and International regulations related to data
        privacy, anti-fraud, payment processing, customs, and logistics.
      </p>

      <hr className="my-4" />

      {/* SECTION 1 */}
      <h3 className="fw-bold">1. Compliance With U.S. Laws & Industry Standards</h3>

      <p className="text-muted">
        SWIFTSHIP complies with all applicable regulations including:
      </p>

      <ul className="text-muted">
        <li>✔ U.S. Customs & Border Protection (CBP) Regulations</li>
        <li>✔ Transportation Security Administration (TSA) guidelines</li>
        <li>✔ Federal Trade Commission (FTC) consumer protection guidelines</li>
        <li>✔ Department of Transportation (DOT) logistics & freight rules</li>
        <li>✔ Anti-Money Laundering (AML) and Know Your Customer (KYC) requirements</li>
        <li>✔ Office of Foreign Assets Control (OFAC) Sanctions Compliance</li>
      </ul>

      <p className="text-muted">
        We ensure that our operations follow strict federal and state-level compliance
        requirements for logistics, warehousing, and freight movement.
      </p>

      <hr className="my-4" />

      {/* SECTION 2 */}
      <h3 className="fw-bold">2. Anti-Fraud & Verification Procedures (AML/KYC)</h3>

      <p className="text-muted">
        To prevent fraud and unauthorized activity, SWIFTSHIP uses multiple layers of security:
      </p>

      <ul className="text-muted">
        <li>✔ Identity verification (ID, business documents, EIN, address checks)</li>
        <li>✔ Fraud and risk scoring for transactions</li>
        <li>✔ IP tracking and device fingerprinting</li>
        <li>✔ Manual review of high-risk or mismatched billing details</li>
        <li>✔ Shipment validation before pickup & dispatch</li>
      </ul>

      <p className="text-muted">
        Any suspicious or high-risk activity may result in temporary holds or cancellation
        of shipments.
      </p>

      <hr className="my-4" />

      {/* SECTION 3 */}
      <h3 className="fw-bold">3. Data Protection & Cybersecurity (PCI-DSS & GDPR)</h3>

      <p className="text-muted">
        SWIFTSHIP follows strict security protocols to protect customer data, including:
      </p>

      <ul className="text-muted">
        <li>✔ Secure servers & encrypted communication (HTTPS / TLS)</li>
        <li>✔ Firewalls & intrusion monitoring</li>
        <li>✔ Limited staff access to sensitive information</li>
        <li>✔ PCI-DSS compliant payment processing</li>
        <li>✔ GDPR & CCPA compliant data handling</li>
        <li>✔ Routine security audits & system monitoring</li>
      </ul>

      <p className="text-muted">
        We do <strong>not</strong> store full credit card numbers or unencrypted sensitive data.
      </p>

      <hr className="my-4" />

      {/* SECTION 4 */}
      <h3 className="fw-bold">4. Customer Identity & Shipment Verification</h3>

      <p className="text-muted">
        Before approving a shipment, we may verify:
      </p>

      <ul className="text-muted">
        <li>✔ Sender identity and contact details</li>
        <li>✔ Business ownership and EIN for commercial accounts</li>
        <li>✔ Package legitimacy, description, and compliance</li>
        <li>✔ Address accuracy for pickup & delivery</li>
      </ul>

      <p className="text-muted">
        These verification steps help prevent illegal or prohibited shipments and ensure logistics integrity.
      </p>

      <hr className="my-4" />

      {/* SECTION 5 */}
      <h3 className="fw-bold">5. Prohibited & Restricted Items</h3>

      <p className="text-muted">
        SWIFTSHIP strictly follows CBP, TSA, DOT, and international carrier restrictions.
        The following cannot be shipped:
      </p>

      <ul className="text-muted">
        <li>✔ Illegal goods & counterfeit products</li>
        <li>✔ Hazardous chemicals (unless pre-approved)</li>
        <li>✔ Weapons, ammunition, or explosive materials</li>
        <li>✔ Perishable items requiring special handling without arrangement</li>
        <li>✔ Items restricted under international trade sanctions</li>
      </ul>

      <p className="text-muted">
        Shipments violating these restrictions may be cancelled or seized by authorities.
      </p>

      <hr className="my-4" />

      {/* SECTION 6 */}
      <h3 className="fw-bold">6. Data Retention & Storage Policy</h3>

      <p className="text-muted">
        For compliance and auditing purposes, we may store shipment-related and customer-related
        records for:
      </p>

      <ul className="text-muted">
        <li>✔ 12–36 months for logistics documentation</li>
        <li>✔ 5 years for AML/KYC requirements (as required by law)</li>
        <li>✔ Until legally required for tax & regulatory audits</li>
      </ul>

      <p className="text-muted">
        Data is securely archived and never sold or shared for marketing without consent.
      </p>

      <hr className="my-4" />

      {/* SECTION 7 */}
      <h3 className="fw-bold">7. Delivery Timeframe Compliance</h3>

      <p className="text-muted">
        SWIFTSHIP complies with logistics operational standards and discloses delivery timelines upfront:
      </p>

      <ul className="text-muted">
        <li>✔ Domestic deliveries: 7–14 business days</li>
        <li>✔ International / consolidated freight: <strong>30–45 days or longer</strong></li>
      </ul>

      <p className="text-muted">
        These timelines vary based on customs, freight availability, routing, and international regulations.
        Customers acknowledge that extended delivery windows do <strong>not</strong> qualify as service failure.
      </p>

      <hr className="my-4" />

      {/* SECTION 8 */}
      <h3 className="fw-bold">8. Chargeback, Dispute & Fraud Prevention Compliance</h3>

      <p className="text-muted">
        To comply with card network & banking regulations, SWIFTSHIP:
      </p>

      <ul className="text-muted">
        <li>✔ Maintains tracking logs, carrier documentation & delivery scans</li>
        <li>✔ Provides complete transparency for dispute resolution</li>
        <li>✔ Requires customers to contact us before filing a chargeback</li>
        <li>✔ Investigates all disputes using factual shipping data</li>
        <li>✔ Suspends accounts involved in fraudulent payment activity</li>
      </ul>

      <p className="text-muted">
        Fraudulent or misuse-based disputes may result in legal action, cost recovery, and reporting
        to payment networks.
      </p>

      <hr className="my-4" />

      {/* SECTION 9 */}
      <h3 className="fw-bold">9. Contact Information</h3>

      <p className="text-muted">
        For compliance, legal, or regulatory questions, contact:
      </p>

      <p className="mt-2">
        <strong>Email:</strong> info@swiftship.us <br />
        <strong>Address:</strong> 3800 Cabell Dr, Irving, TX 75063, United States <br />
        <strong>Phone:</strong> (480) 520-0274
      </p>

    </div>
  );
}
